import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeft } from "lucide-react";
import { getQuoteByID, updateQuoteStatus } from "../../reducers/quote.slice";
import { convertDateFormatInUtc, formatNumberToDollar } from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const FALLBACK_IMG = "https://cdn.pixabay.com/photo/2024/06/26/23/36/package-8856091_1280.png";

export default function QuoteDetailContent({ quoteId, showChrome = true, onSuccess, onCancel }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const resolvedQuoteId = quoteId || location?.state?.quoteId;
  const { showToast } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quoteData, setQuoteData] = useState({});
  const [openAccept, setOpenAccept] = useState(false);
  const [openReject, setOpenReject] = useState(false);
  const [rejection, setRejection] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [imgOpen, setImgOpen] = useState(false);

  const handleBack = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate(-1);
  };

  function isExpiryDateValid(expiryDateUTCString) {
    try {
      return new Date() < new Date(`${expiryDateUTCString}Z`);
    } catch (error) {
      console.error("Error comparing expiry time:", error);
      return false;
    }
  }

  const handleUpdateQuote = (action) => {
    const payload = { id: quoteData?.id, status: action, notes: rejection };
    if (action === "QUOTE_ACCEPTED") delete payload.notes;
    setSubmitting(true);
    dispatch(updateQuoteStatus(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setSubmitting(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setSubmitting(false);
      if (onSuccess) {
        onSuccess();
        return;
      }
      navigate(-1);
    });
  };

  useEffect(() => {
    if (!resolvedQuoteId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    dispatch(getQuoteByID(resolvedQuoteId)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setLoading(false);
      const expiryDateFromApi = res?.payload?.quote_expiry;
      setIsValid(expiryDateFromApi ? isExpiryDateValid(expiryDateFromApi) : null);
      setQuoteData({ ...res?.payload });
    });
  }, [dispatch, resolvedQuoteId]);

  const showExpiryBanner =
    quoteData?.status !== "QUOTE_PENDING" &&
    quoteData?.status !== "QUOTE_REJECTED" &&
    quoteData?.status !== "QUOTE_ACCEPTED" &&
    quoteData?.status !== "QUOTE_DENIED";
  const showActions = quoteData?.status === "QUOTE_APPROVED" && isValid;

  return (
    <div style={{ maxWidth: showChrome ? 560 : "100%", margin: "0 auto" }}>
      {showChrome && (
        <>
          <button
            type="button"
            onClick={handleBack}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "transparent",
              border: "none",
              color: "rgba(255,255,255,0.6)",
              fontSize: 13,
              cursor: "pointer",
              padding: 0,
              marginBottom: 18,
            }}
          >
            <ArrowLeft size={15} /> Back
          </button>

          <PageHeader title="Quote Details" subtitle="Review the details of your quoted item." />
        </>
      )}

      {loading ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "#c3c8cc", fontSize: 13.5 }}>Loading...</div>
      ) : (
        <div style={{ marginTop: showChrome ? 20 : 0, display: "flex", flexDirection: "column", gap: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: 16,
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
              background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
            }}
          >
            <button
              type="button"
              onClick={() => quoteData?.item_image_url && setImgOpen(true)}
              style={{
                width: 70,
                height: 70,
                borderRadius: 10,
                backgroundImage: `url(${quoteData?.item_image_url || FALLBACK_IMG})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid rgba(255,255,255,0.1)",
                flexShrink: 0,
                padding: 0,
                cursor: quoteData?.item_image_url ? "pointer" : "default",
              }}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>{quoteData.item_name}</div>
              <div style={{ fontSize: 12.5, color: "#c3c8cc", marginTop: 3 }}>Quantity: {quoteData.quantity}</div>
              <div style={{ fontSize: 12.5, color: "#c3c8cc", marginTop: 2 }}>UPC #{quoteData.upc}</div>
              {quoteData.item_link && (
                <a
                  href={quoteData.item_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 12.5, color: "#00ADB5", marginTop: 4, display: "inline-block" }}
                >
                  Item Link
                </a>
              )}
            </div>
            <StatusBadge status={quoteData?.status} />
          </div>

          {showExpiryBanner && (
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                fontSize: 12.5,
                lineHeight: 1.5,
                color: !isValid ? "#f27059" : "#e8b84b",
                background: !isValid ? "rgba(242,112,89,0.1)" : "rgba(232,184,75,0.1)",
              }}
            >
              {quoteData?.quote_expiry
                ? !isValid
                  ? `This quote has expired on ${convertDateFormatInUtc(quoteData.quote_expiry, "MMMM D, YYYY hh:mm:ss A")}.`
                  : `This quote will expire on ${convertDateFormatInUtc(quoteData.quote_expiry, "MMMM D, YYYY hh:mm:ss A")}.`
                : "No expiration date specified."}
            </div>
          )}

          <div
            style={{
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
              padding: "4px 18px",
              background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 18,
                padding: "13px 0",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                fontSize: 13,
              }}
            >
              <span style={{ color: "#c3c8cc" }}>RSC Cash Out Price (per unit)</span>
              <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace" }}>
                {formatNumberToDollar(quoteData?.cashout_price)}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 18, padding: "13px 0", fontSize: 15, fontWeight: 700 }}>
              <span style={{ color: "#fff" }}>Total Payout</span>
              <span style={{ color: "#4caf50", fontFamily: "ui-monospace, monospace" }}>
                {formatNumberToDollar(quoteData?.total_amount)}
              </span>
            </div>
            {quoteData?.notes && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 18,
                  padding: "13px 0",
                  borderTop: "1px solid rgba(255,255,255,0.05)",
                  fontSize: 13,
                }}
              >
                <span style={{ color: "#c3c8cc" }}>Reason of Rejection</span>
                <span style={{ color: "#fff", textAlign: "right", overflowWrap: "anywhere" }}>{quoteData.notes}</span>
              </div>
            )}
          </div>

          {showActions && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <Button variant="destructive" onClick={() => setOpenReject(true)}>
                Deny Quote
              </Button>
              <Button onClick={() => setOpenAccept(true)}>Accept Quote</Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={openAccept} onOpenChange={setOpenAccept}>
        <DialogContent style={{ maxWidth: 440 }}>
          <DialogHeader>
            <DialogTitle>Confirm Quote Acceptance</DialogTitle>
          </DialogHeader>
          <p style={{ fontSize: 13, color: "#c3c8cc", lineHeight: 1.6 }}>
            You are about to accept this quote for {quoteData.item_name}. Once accepted, the order will proceed to
            the shipping process. Do you want to continue?
          </p>
          <DialogFooter style={{ gap: 8 }}>
            <Button variant="outline" onClick={() => setOpenAccept(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={() => handleUpdateQuote("QUOTE_ACCEPTED")} disabled={submitting}>
              {submitting ? "Accepting..." : "Accept Quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openReject} onOpenChange={setOpenReject}>
        <DialogContent style={{ maxWidth: 440 }}>
          <DialogHeader>
            <DialogTitle>Confirm Quote Rejection</DialogTitle>
          </DialogHeader>
          <p style={{ fontSize: 13, color: "#c3c8cc", lineHeight: 1.6, marginBottom: 12 }}>
            You are about to reject this quote. Once rejected, the order will be closed, and no further action can
            be taken. Are you sure you want to proceed?
          </p>
          <Label htmlFor="rejection">Reason of Rejection</Label>
          <Textarea
            id="rejection"
            value={rejection}
            onChange={(e) => setRejection(e.target.value)}
            placeholder="Provide a reason for rejection"
            rows={4}
          />
          <DialogFooter style={{ gap: 8 }}>
            <Button variant="outline" onClick={() => setOpenReject(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleUpdateQuote("QUOTE_DENIED")} disabled={submitting}>
              {submitting ? "Denying..." : "Deny Quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={imgOpen} onOpenChange={setImgOpen}>
        <DialogContent style={{ maxWidth: 420, background: "rgba(0,0,0,0.9)", padding: 16 }}>
          <img
            src={quoteData?.item_image_url}
            alt={quoteData?.item_name}
            style={{ width: "100%", borderRadius: 10, objectFit: "contain", maxHeight: "70vh" }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
